import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Cell } from '@ff-os/shared-react/components/grid/Cell';
import { Grid } from '@ff-os/shared-react/components/grid/Grid';
import { GiftPdpState } from '@ff-scene-plugins/gift-redux/store';
import classNames from 'classnames';
import styles from './ImageSlider.module.scss';

type Props = {
    images: Array<{
        url?: string;
        alt?: string;
        isLivePreview?: boolean;
        renderPreview?: () => React.ReactNode;
    }>;
};

const selectInputsTrigger = (state: GiftPdpState) => {
    const inputs = state.project.inputs;
    const inputValues = inputs.ids.map(id => {
        const input = inputs.entities[id];
        return input ? `${id}:${JSON.stringify(input.value)}` : '';
    });
    return inputValues.join('|');
};

export const ImageSlider = ({ images }: Props) => {

    const [selectedIndex, setSelectedIndex] = useState(0);
    const inputsTrigger = useSelector(selectInputsTrigger);
    const previousTriggerRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (selectedIndex >= images.length) {
            setSelectedIndex(Math.max(0, images.length - 1));
        }
    }, [images, selectedIndex]);

    // Switch to live preview when inputs change (after user has made a change)
    useEffect(() => {
        // On first mount, just store the initial trigger
        if (previousTriggerRef.current === undefined) {
            previousTriggerRef.current = inputsTrigger;
            return;
        }

        // If trigger changed, switch to live preview
        if (inputsTrigger !== previousTriggerRef.current) {
            previousTriggerRef.current = inputsTrigger;

            const livePreviewIndex = images.findIndex(img => img.isLivePreview);
            if (livePreviewIndex !== -1 && selectedIndex !== livePreviewIndex) {
                setSelectedIndex(livePreviewIndex);
            }
        }
    }, [inputsTrigger, images, selectedIndex]);

    if (!images || images.length === 0) {
        return null;
    }

    const safeIndex = Math.min(Math.max(0, selectedIndex), images.length - 1);
    const selectedImage = images[safeIndex];

    if (!selectedImage) {
        return null;
    }

    return (
        <div className={styles.imageSlider}>
            <Grid direction='row' gap='m' className={styles.container}>
                {/* Thumbnail column on the left */}
                <Cell className={styles.thumbnailColumn}>
                    <Grid direction='column' gap='s'>
                        {images.map((image, index) => (
                            <Cell key={index}>
                                <button
                                    className={classNames(styles.thumbnail, {
                                        [styles.thumbnailActive]: index === safeIndex
                                    })}
                                    onClick={() => setSelectedIndex(index)}
                                >
                                    {image.isLivePreview && image.renderPreview ? (
                                        <div className={styles.thumbnailPreview}>
                                            {image.renderPreview()}
                                        </div>
                                    ) : image.url ? (
                                        <img
                                            crossOrigin='anonymous'
                                            src={image.url}
                                            alt={image.alt || `Preview ${index + 1}`}
                                            className={styles.thumbnailImage}
                                        />
                                    ) : null}
                                </button>
                            </Cell>
                        ))}
                    </Grid>
                </Cell>

                {/* Large preview on the right */}
                <Cell className={styles.mainImageColumn}>
                    <div className={styles.mainImageContainer}>
                        {selectedImage.isLivePreview && selectedImage.renderPreview ? (
                            <div className={styles.mainPreview}>
                                {selectedImage.renderPreview()}
                            </div>
                        ) : selectedImage.url ? (
                            <img
                                crossOrigin='anonymous'
                                src={selectedImage.url}
                                alt={selectedImage.alt || 'Preview'}
                                className={styles.mainImage}
                            />
                        ) : null}
                    </div>
                </Cell>
            </Grid>
        </div>
    );
};